import { get as getLocal, set as setLocal } from "hassibot/local-storage";
import { serviceWorkerRegistration } from "hassibot/services_v2/service-worker/service-worker";
import {
  get as getSession,
  remove as removeSession,
  set as setSession,
} from "hassibot/session-storage";
import { DateTime } from "luxon";
import {
  CHECK_FOR_UPDATE,
  COLLECT_UNSAVED_WORK,
  mtLogger,
  SKIP_WAITING,
  UNSAVED_WORK_QUERY,
  UNSAVED_WORK_REPLY,
} from "shared-core";

import { isDeploymentOptInEnabled } from "./deployment-opt-in-preference";
import { hasUnsavedWork } from "./unsaved-work-registry";

// How long the originating tab waits for the aggregated unsaved-work list.
const COLLECT_TIMEOUT_MS = 2000;
// localStorage key holding the ISO day (yyyy-MM-dd) of the user's last activity,
// used to detect a "morning return" (first activity on a new calendar day).
const LAST_ACTIVE_DAY_KEY = "HB_LAST_ACTIVE_DAY";
// sessionStorage flag (per tab) telling the just-reloaded page to show a success
// toast — set on every apply path (morning silent, footer button, modal confirm).
const JUST_UPDATED_KEY = "HB_JUST_UPDATED";
// After a morning return, how long we keep auto-applying a surfacing update before
// falling back to the discreet footer button. Bounds the silent reload to the
// genuine "just came back" moment — a mid-afternoon deploy must not auto-reload.
const MORNING_WINDOW_MS = 2 * 60 * 1000;

/**
 * Is a detected version actually applicable through the opt-in flow?
 *
 * Pure decision, extracted for testing:
 * - no controller → first install, not an update → nothing to apply;
 * - no waiting worker → nothing to apply;
 * - opt-in disabled → we auto-activate instead (default behaviour) → not surfaced.
 */
export const isUpdateApplicable = ({
  hasController,
  hasWaiting,
  isOptIn,
}: {
  hasController: boolean;
  hasWaiting: boolean;
  isOptIn: boolean;
}): boolean => hasController && hasWaiting && isOptIn;

/**
 * Has the user come back on a *new calendar day* since their last recorded
 * activity? Pure (inputs passed in) so it is trivially testable. A missing
 * `lastActiveDay` (first ever session / post-nuke) returns false: we never
 * surprise-reload when we cannot tell how long the user has been away. The ISO
 * `yyyy-MM-dd` format sorts chronologically, so a `<` comparison is correct (and
 * a backwards clock reads as "not a new day", which is the safe direction).
 */
export const isNewCalendarDay = ({
  lastActiveDay,
  today,
}: {
  lastActiveDay: string | null;
  today: string;
}): boolean => lastActiveDay !== null && lastActiveDay < today;

// --- External store (useSyncExternalStore) -------------------------------------

export type UnsavedWorkCollection = {
  // Labels of every tab holding unsaved work that the update will reload —
  // including this (emitter) tab, which also reloads.
  tabs: string[];
  // True when at least one tab didn't answer in time (SW-side or overall timeout),
  // so the list may be missing entries — never just because the list is non-empty.
  mayBeIncomplete: boolean;
};

/**
 * May a morning auto-upgrade reload silently? Only when we are *sure* nothing is
 * lost: no tab reported unsaved work AND every tab answered (an incomplete
 * collection is treated as unsafe → we ask for confirmation instead). Pure, so it
 * is trivially testable.
 */
export const isSafeToAutoApply = ({ tabs, mayBeIncomplete }: UnsavedWorkCollection): boolean =>
  tabs.length === 0 && !mayBeIncomplete;

export type AppVersionUpdateSnapshot = {
  // Drives the discreet footer "Mettre à jour" button.
  isUpdateAvailable: boolean;
  // Non-null while the confirmation modal should be shown — set by an explicit
  // footer click or by a morning return that found unsaved work. Holds the
  // collected work to warn about.
  pendingConfirm: UnsavedWorkCollection | null;
};

let snapshot: AppVersionUpdateSnapshot = { isUpdateAvailable: false, pendingConfirm: null };
const listeners = new Set<() => void>();

const notify = (): void => listeners.forEach(listener => listener());

// Replace the snapshot, keeping a stable reference unless something actually
// changed (required by useSyncExternalStore to avoid render loops).
const patchSnapshot = (patch: Partial<AppVersionUpdateSnapshot>): void => {
  const next = { ...snapshot, ...patch };
  if (
    next.isUpdateAvailable === snapshot.isUpdateAvailable &&
    next.pendingConfirm === snapshot.pendingConfirm
  ) {
    return;
  }
  snapshot = next;
  notify();
};

const setIsUpdateAvailable = (value: boolean): void => patchSnapshot({ isUpdateAvailable: value });

export const appVersionUpdateStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  getSnapshot(): AppVersionUpdateSnapshot {
    return snapshot;
  },
};

// --- Update detection & application ---------------------------------------------

let registration: ServiceWorkerRegistration | null = null;
// Anti-loop guard for the single controllerchange listener below.
let refreshing = false;
// Timestamp of the last morning return (0 = none). The silent auto-upgrade is only
// attempted while within MORNING_WINDOW_MS of it (see isMorningWindowOpen).
let morningReturnAt = 0;
// Guard so a single tab never runs two overlapping auto-upgrade attempts (e.g. an
// already-waiting worker at boot + an updatefound moments later).
let autoUpgradeInProgress = false;

const todayIso = (): string => DateTime.now().toISODate();

const isMorningWindowOpen = (): boolean =>
  morningReturnAt !== 0 && Date.now() - morningReturnAt < MORNING_WINDOW_MS;

/**
 * Detect a morning return (first activity on a new calendar day, opt-in only) and
 * record today as the last-active day. Recording also "consumes" the staleness,
 * which prevents a reload loop: after the coordinated reload, this same-day boot no
 * longer counts as a new day, so nothing re-arms.
 */
const refreshMorningReturn = (): void => {
  const today = todayIso();
  const lastActiveDay = getLocal(LAST_ACTIVE_DAY_KEY);
  if (isDeploymentOptInEnabled() && isNewCalendarDay({ lastActiveDay, today })) {
    morningReturnAt = Date.now();
    mtLogger.debug("Morning return detected — arming auto-upgrade window");
  }
  setLocal(LAST_ACTIVE_DAY_KEY, today);
};

/**
 * Ask the waiting worker to activate. The resulting `controllerchange` reloads
 * every controlled tab (handled by the module-level listener below). Flags a
 * success toast for after the reload on this tab only (per-tab sessionStorage
 * survives its own reload; other reloaded tabs never set it, so no toast storm).
 */
const confirmAndApply = (): void => {
  const waiting = registration?.waiting;
  if (!waiting) return;
  setSession(JUST_UPDATED_KEY, "true");
  waiting.postMessage({ type: SKIP_WAITING });
};

/**
 * Ask the active Service Worker to check for a newer version. The SW owns the
 * actual registration.update() call and throttles it globally across tabs.
 */
const requestUpdateCheck = (reg: ServiceWorkerRegistration): void => {
  const worker = reg.active ?? navigator.serviceWorker?.controller;
  worker?.postMessage({ type: CHECK_FOR_UPDATE });
};

// The app's base document.title (index.html). A page that sets no <HeadTitle>
// keeps this, so we treat it as "no specific title" and fall back to the app name.
const BASE_TITLE = "Hassibot";

/**
 * One recognisable label for a tab holding unsaved work. Prefer the page title
 * (set via <HeadTitle>/Helmet) — what the user sees in their tab bar, so it
 * points them straight at the right window. Pages that set no title keep the
 * base "Hassibot"; there we fall back to the app name derived from the URL (apps
 * live under /ia/<app>/…) so the user at least knows which app. Empty array when
 * nothing is unsaved. Pure (globals passed in) so it is trivially testable.
 */
export const unsavedWorkLabel = ({
  hasUnsaved,
  title,
  pathname,
}: {
  hasUnsaved: boolean;
  title: string;
  pathname: string;
}): string[] => {
  if (!hasUnsaved) return [];
  const cleanTitle = title.trim();
  if (cleanTitle && cleanTitle !== BASE_TITLE) return [cleanTitle];
  const appSlug = pathname.match(/^\/ia\/([^/]+)/)?.[1];
  return [appSlug ? appSlug.charAt(0).toUpperCase() + appSlug.slice(1) : BASE_TITLE];
};

// Live wrapper: this tab's label from its current title/location.
const localUnsavedWorkLabels = (): string[] =>
  unsavedWorkLabel({
    hasUnsaved: hasUnsavedWork(),
    title: document.title,
    pathname: window.location.pathname,
  });

/**
 * Best-effort, non-blocking collection of unsaved work across all tabs that the
 * update will reload. This (emitter) tab's own entry is always included (read
 * locally), then merged with the *other* tabs' entries gathered by the Service
 * Worker — which excludes the emitter from its relay to avoid double-counting.
 * `mayBeIncomplete` reflects a real timeout (a tab that didn't answer), not the
 * mere presence of entries.
 */
const collectUnsavedWork = (): Promise<UnsavedWorkCollection> => {
  // The current tab reloads too, so its unsaved work must be surfaced.
  const localTabs = localUnsavedWorkLabels();
  const controller = navigator.serviceWorker?.controller;
  // No controller (e.g. first install) → no cross-tab channel; report only local.
  if (!controller) return Promise.resolve({ tabs: localTabs, mayBeIncomplete: false });

  return new Promise<UnsavedWorkCollection>(resolve => {
    const channel = new MessageChannel();
    // No reply at all (e.g. an old controller without this handler) → incomplete.
    const timeout = setTimeout(
      () => resolve({ tabs: localTabs, mayBeIncomplete: true }),
      COLLECT_TIMEOUT_MS
    );
    channel.port1.onmessage = (event): void => {
      clearTimeout(timeout);
      const otherTabs = Array.isArray(event.data?.tabs) ? event.data.tabs : [];
      // Dedupe: several tabs on the same page share a title, and listing it once
      // is enough to point the user there.
      resolve({
        tabs: [...new Set([...localTabs, ...otherTabs])],
        mayBeIncomplete: !!event.data?.mayBeIncomplete,
      });
    };
    controller.postMessage({ type: COLLECT_UNSAVED_WORK }, [channel.port2]);
  });
};

/**
 * Morning-return path: try to apply a waiting update without bothering the user.
 * If no tab holds unsaved work (and we could confirm it), reload silently.
 * Otherwise surface the confirmation modal so the user decides. One attempt only —
 * the morning window is consumed.
 */
const attemptAutoUpgrade = async (): Promise<void> => {
  if (autoUpgradeInProgress) return;
  autoUpgradeInProgress = true;
  morningReturnAt = 0; // consume the window: a single attempt per morning return
  try {
    const collection = await collectUnsavedWork();
    if (isSafeToAutoApply(collection)) {
      mtLogger.debug("Morning return — no unsaved work, applying update silently");
      confirmAndApply();
    } else {
      mtLogger.debug("Morning return — unsaved work found, asking for confirmation");
      patchSnapshot({ pendingConfirm: collection });
    }
  } finally {
    autoUpgradeInProgress = false;
  }
};

/**
 * React to a (possibly) newly installed worker waiting to take over. In opt-in
 * mode we surface the footer button and, right after a morning return, try the
 * silent auto-upgrade. Otherwise we reproduce today's behaviour by asking the
 * waiting worker to activate immediately (no button, no reload).
 */
const evaluateWaiting = (reg: ServiceWorkerRegistration): void => {
  const hasController = !!navigator.serviceWorker.controller;
  const hasWaiting = !!reg.waiting;
  const isOptIn = isDeploymentOptInEnabled();

  if (isUpdateApplicable({ hasController, hasWaiting, isOptIn })) {
    mtLogger.debug("New Service Worker waiting — surfacing manual update");
    setIsUpdateAvailable(true);
    if (isMorningWindowOpen()) void attemptAutoUpgrade();
  } else if (hasController && hasWaiting && !isOptIn) {
    mtLogger.debug("New Service Worker waiting — auto-activating (default mode)");
    reg.waiting?.postMessage({ type: SKIP_WAITING });
  }
};

const watchInstalling = (reg: ServiceWorkerRegistration): void => {
  const installing = reg.installing;
  if (!installing) return;
  installing.addEventListener("statechange", () => {
    if (installing.state === "installed") {
      evaluateWaiting(reg);
    }
  });
};

const initRegistration = (reg: ServiceWorkerRegistration): void => {
  registration = reg;

  // Detect a morning return up front so an update that arrived overnight (already
  // waiting, or fetched by the update() below) can be auto-applied.
  refreshMorningReturn();

  // A new version may already be waiting when the tab boots (update arrived while
  // the tab was closed / in another tab).
  if (reg.waiting) {
    evaluateWaiting(reg);
  }
  // Ask the active SW to check for a newer version now; the SW throttles the
  // actual /sw.js refetch globally (including across tabs and fetch triggers).
  requestUpdateCheck(reg);

  reg.addEventListener("updatefound", () => watchInstalling(reg));

  // No teardown: this runs once for the module singleton and lives for the whole
  // page lifetime (the registration promise resolves a single time), so the
  // listener is intentionally never cleared.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    // Coming back to the tab is the moment to (re)detect a morning return.
    refreshMorningReturn();
    // An update may already be waiting from earlier this session → evaluate now
    // (also auto-upgrades if the morning window just opened).
    if (reg.waiting) evaluateWaiting(reg);
    // The SW owns the throttle, so every visible transition should request a
    // freshness check.
    requestUpdateCheck(reg);
  });
};

// --- Public actions -------------------------------------------------------------

/** Explicit user request (footer button): always confirm via the modal. */
const requestUpdate = async (): Promise<void> => {
  const collection = await collectUnsavedWork();
  patchSnapshot({ pendingConfirm: collection });
};

/** Confirm from the modal → apply the coordinated update (reloads every tab). */
const confirmUpdate = (): void => {
  patchSnapshot({ pendingConfirm: null });
  confirmAndApply();
};

/** Dismiss the confirmation modal without applying. */
const dismissUpdate = (): void => patchSnapshot({ pendingConfirm: null });

/**
 * Read-and-clear the per-tab "just updated" flag, so the tab that triggered an
 * update (morning silent, footer button, or modal confirm) shows a success toast
 * exactly once after the reload.
 */
export const consumeUpdateToast = (): boolean => {
  const shouldToast = getSession(JUST_UPDATED_KEY) === "true";
  if (shouldToast) removeSession(JUST_UPDATED_KEY);
  return shouldToast;
};

export const appVersionUpdateActions = { requestUpdate, confirmUpdate, dismissUpdate };

// --- Side-effect wiring (guarded so the module is import-safe in tests/dev) ------

if (typeof navigator !== "undefined" && navigator.serviceWorker) {
  // Single controllerchange listener: when the new worker takes control, reload
  // every tab — but only in opt-in mode (default mode's auto-activation must not
  // reload). The `refreshing` flag prevents a reload loop.
  //
  // Reading the opt-in live here is coherent with the apply intent because the
  // preference is *one-way* within a session (the footer toggle only enables it,
  // never disables — see build-hash.jsx): a tab that armed an update cannot have
  // flipped back to non-opt-in between the click and this event.
  //
  // This coordinated reload also relies on the SW NOT calling `clients.claim()`
  // (it doesn't — see sw.ts / workbox): `controllerchange` then only fires on an
  // already-controlled tab, never on a brand-new first install. If `clients.claim()`
  // is ever added, an uncoordinated activation would reload opt-in tabs — revisit
  // this guard then.
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) return;
    if (!isDeploymentOptInEnabled()) return;
    refreshing = true;
    // Disarm the native "unsaved changes" prompt so the coordinated reload is
    // silent (the user already confirmed, or had no unsaved work).
    window.onbeforeunload = null;
    window.location.reload();
  });

  // Reply to the Service Worker's relayed unsaved-work query with this tab's
  // registry contents.
  navigator.serviceWorker.addEventListener("message", event => {
    if (event.data?.type === UNSAVED_WORK_QUERY) {
      event.ports[0]?.postMessage({ type: UNSAVED_WORK_REPLY, labels: localUnsavedWorkLabels() });
    }
  });
}

// `serviceWorkerRegistration` is undefined outside prod/test-env builds, so this
// is a no-op in dev and in unit tests.
serviceWorkerRegistration?.then(initRegistration).catch(() => {
  /* SW unsupported / registration failed — feature stays inert */
});
